import {useCallback} from 'react';
import {useAppSelector} from '@src/Store/hooks';
import {
  useLazyCreateMemberSavedQuery,
  useLazyDeleteMemberSavedByContentIdQuery,
  useLazyCountSavedByIdQuery,
} from '@src/Api/TraceApi/traceApi';
import {useDispatch} from 'react-redux';
import memberSlice from '@src/Store/Slices/Member.slice';
import {RouteTypename} from '@src/Navigation/types/route.type';
import {ContentTypeRaw} from '@src/Interfaces/ContentTypeRaw';
import {trackEvent} from '@src/Tracking/trackEvent';
import {TraceTokEvents} from '@src/Tracking/catalog';

export interface IBookmarkItem {
  id: string;
  url: string;
  __typename: RouteTypename;
  name?: string;
  categoryName?: string;
  raw?: ContentTypeRaw;
}

/**
 * Hook sécurisé pour gérer les bookmarks
 * Utilise authGuardBaseQuery automatiquement - plus besoin de logique d'auth manuelle
 */
export const useSecuredBookmarks = () => {
  const dispatch = useDispatch();

  // API hooks - maintenant sécurisés automatiquement via authGuardBaseQuery
  const [createBookmark, {isLoading: isCreating}] =
    useLazyCreateMemberSavedQuery();
  const [deleteBookmark, {isLoading: isDeleting}] =
    useLazyDeleteMemberSavedByContentIdQuery();
  const [fetchCount] = useLazyCountSavedByIdQuery();

  // State des bookmarks depuis Redux
  const {bookmarks = []} = useAppSelector(({member: memberState}) => ({
    bookmarks: memberState.saved,
  }));

  // Vérifier si un item est bookmarké
  const isBookmarked = useCallback(
    (contentId: string): boolean => {
      return bookmarks.some(bookmark => bookmark.contentId === contentId);
    },
    [bookmarks],
  );

  // Trouver un bookmark par contentId
  const findBookmark = useCallback(
    (contentId: string) => {
      return bookmarks.find(bookmark => bookmark.contentId === contentId);
    },
    [bookmarks],
  );

  // Toggle bookmark - Version simplifiée
  const toggleBookmark = useCallback(
    async (contentId: string, item: IBookmarkItem, shouldAdd?: boolean) => {
      const isCurrentlyBookmarked = isBookmarked(contentId);
      const shouldBookmark =
        shouldAdd !== undefined ? shouldAdd : !isCurrentlyBookmarked;

      try {
        if (shouldBookmark && !isCurrentlyBookmarked) {
          // Ajouter bookmark - L'auth est gérée automatiquement par authGuardBaseQuery
          const response = await createBookmark({
            data: {
              contentId: item.id,
              contentSource: 'watch', // Déterminé dynamiquement selon le type de contenu
            },
          });

          if (!response.error && response.data) {
            dispatch(memberSlice.actions.addSaved(response.data));
            dispatch(memberSlice.actions.addBookmarkCount({contentId, add: 1}));
            // bookmark_added (canonical) — via the module bridge so it fires
            // from lazy player chunks too.
            trackEvent(TraceTokEvents.BookmarkAdded, {
              content_id: contentId,
              content_type: item.__typename,
              placement: 'player',
            });
          }
        } else if (!shouldBookmark && isCurrentlyBookmarked) {
          // Supprimer bookmark - L'auth est gérée automatiquement par authGuardBaseQuery
          const response = await deleteBookmark({contentId});

          if (!response.error) {
            const existingBookmark = findBookmark(contentId);
            if (existingBookmark) {
              dispatch(memberSlice.actions.removeSaved({contentId}));
              dispatch(
                memberSlice.actions.addBookmarkCount({contentId, add: -1}),
              );
            }
            // bookmark_removed (canonical)
            trackEvent(TraceTokEvents.BookmarkRemoved, {
              content_id: contentId,
              content_type: item.__typename,
            });
          }
        }
      } catch (error) {
        console.error('Erreur lors du toggle bookmark:', error);
        // L'erreur d'auth sera gérée automatiquement par authGuardBaseQuery
      }
    },
    [createBookmark, deleteBookmark, dispatch, isBookmarked, findBookmark],
  );

  // Version simplifiée pour les composants existants
  const simpleToggle = useCallback(
    (contentId: string, shouldAdd: boolean) => {
      // Créer un item basique pour les bookmarks existants
      const basicItem: IBookmarkItem = {
        id: contentId,
        url: '', // L'URL n'est plus nécessaire avec authGuardBaseQuery
        __typename: 'Content' as RouteTypename, // Type générique
      };

      toggleBookmark(contentId, basicItem, shouldAdd);
    },
    [toggleBookmark],
  );

  // Récupérer le nombre de likes pour un contenu
  const fetchBookmarkCount = useCallback(
    async (contentId: string) => {
      try {
        const {data} = await fetchCount({id: contentId});
        if (data) {
          dispatch(
            memberSlice.actions.setBookmarkCount({
              contentId,
              count: data.total,
            }),
          );
        }
      } catch (error) {
        // L'erreur d'auth sera gérée automatiquement par authGuardBaseQuery
        console.error('Erreur lors de la récupération du count:', error);
      }
    },
    [fetchCount, dispatch],
  );

  return {
    // État
    isBookmarked,
    findBookmark,
    bookmarks,

    // Actions
    toggleBookmark,
    simpleToggle, // Pour remplacer facilement les usages existants
    fetchBookmarkCount,

    // Loading states
    isLoading: isCreating || isDeleting,
    isCreating,
    isDeleting,
  };
};

export default useSecuredBookmarks;
